/**
 * @file
 * Drupal behavior for the layout plan component.
 *
 * @description
 * Reads the shapes JSON from a data attribute, normalizes the tree,
 * and hydrates the static Twig shell with an interactive SVG canvas,
 * sidebar shape list, and detail panel.
 */

import { normalizeLayoutPlanRoots, parseLayoutPlanShapes } from './src/_normalize';
import { LayoutPlanStateManager } from './src/_state';
import {
  renderCanvas,
  renderShapeList,
  renderShapeListSkeletons,
  renderPanel,
  renderBreadcrumbs,
  renderPlanSwitcher,
  renderSidebarHeader,
  togglePolygonModifier,
  planLabel,
} from './src/_renderers';

((Drupal, once) => {
  'use strict';

  /**
   * Initializes the interactive layout plan.
   *
   * @description
   * Parses the shapes JSON from data-shapes, builds the normalized top-level
   * plans, and renders SVG polygons, a sidebar shape list, and property detail
   * panel.
   */
  Drupal.behaviors.tpLayoutPlan = {
    attach(context: Document | HTMLElement) {
      const elements = once('tp-layout-plan', '.layout-plan', context);

      elements.forEach((element) => {
        const container = element as HTMLElement;
        const raw = container.dataset.shapes;
        if (!raw) return;

        const rootLayers = normalizeLayoutPlanRoots(parseLayoutPlanShapes(raw));
        if (rootLayers.length === 0) return;

        const projectName = container.dataset.projectName ?? '';

        // Grab DOM containers rendered by the Twig template.
        const canvasEl = container.querySelector('.layout-plan__canvas') as HTMLElement;
        const shapeListEl = container.querySelector('.layout-plan__shape-list') as HTMLElement;
        const panelEl = container.querySelector('.layout-plan__panel') as HTMLElement;
        const promptEl = container.querySelector('.layout-plan__sidebar-prompt') as HTMLElement;
        const backEl = container.querySelector('.layout-plan__sidebar-back') as HTMLElement;
        const breadcrumbEl = container.querySelector('.layout-plan__breadcrumbs') as HTMLElement;
        const plansEl = container.querySelector('.layout-plan__plans') as HTMLElement;

        if (!canvasEl || !shapeListEl || !panelEl || !promptEl || !backEl || !breadcrumbEl || !plansEl) return;

        const defaultPrompt = promptEl.textContent ?? Drupal.t('Select an element');

        // Wire the back button click (rendered by Twig, wired once here).
        const backBtn = backEl.querySelector('.layout-plan__back-btn') as HTMLElement | null;

        // Full re-render helper for navigation changes.
        function renderAll(): void {
          // Ensure shape list is visible (may have been hidden by property selection).
          shapeListEl.removeAttribute('hidden');
          // Show skeleton placeholders while the canvas image loads.
          renderShapeListSkeletons(shapeListEl, state);
          renderCanvas(canvasEl, state, () => {
            renderShapeList(shapeListEl, state);
          });
          renderPanel(panelEl, state);
          renderPlanSwitcher(plansEl, state);
          renderBreadcrumbs(breadcrumbEl, state);
          renderSidebarHeader(promptEl, backEl, state, defaultPrompt);

          // Name the plan when there is more than one, so switching plans and
          // drilling down are distinguishable without sight.
          const layers = state.rootLayers;
          const activeLayer = layers[state.activeLayerIndex];
          Drupal.announce(
            layers.length > 1 && activeLayer
              ? Drupal.t('Layout plan updated: @plan', {
                  '@plan': planLabel(activeLayer, state.activeLayerIndex),
                })
              : Drupal.t('Layout plan updated')
          );
        }

        const state = new LayoutPlanStateManager(rootLayers, projectName, {
          onHoverChange(oldId, newId) {
            togglePolygonModifier(container, oldId, newId, 'layout-plan__polygon--hover');
            togglePolygonModifier(container, oldId, newId, 'layout-plan__shape-btn--hover');
          },
          onSelectChange(oldId, newId) {
            togglePolygonModifier(container, oldId, newId, 'layout-plan__polygon--selected');
            // When a property is selected, hide the shape list and show the panel.
            // When deselected, restore the shape list.
            if (newId && state.selectedPolygon?.property) {
              shapeListEl.setAttribute('hidden', '');
            } else {
              shapeListEl.removeAttribute('hidden');
            }
            // Re-render panel, breadcrumbs, and sidebar header (back button visibility).
            renderPanel(panelEl, state);
            renderBreadcrumbs(breadcrumbEl, state);
            renderSidebarHeader(promptEl, backEl, state, defaultPrompt);
          },
          onNavigate() {
            renderAll();
          },
        });

        // Initial render.
        renderAll();

        // Wire the back button click (rendered by Twig, wired once here).
        // If a property is selected, deselect it first (return to shape list).
        // Otherwise, go back one level in the drill-down history.
        if (backBtn) {
          backBtn.addEventListener('click', () => {
            if (state.selectedPolygon?.property) {
              state.select(null);
            } else {
              state.goBack();
            }
          });
        }
      });
    },
  };
})(Drupal, once);
